import { z } from 'zod';
import { toIssues } from './materials/catalog.js';
import type { ParseResult } from './voyage/schemas.js';

/**
 * Crew List (ortak 2026-10-26 madde 4; ADR 0130 §B): IMO FAL Form 5 "Crew List" alanları, düz
 * biçim (şirket üst ve alt bilgisi yok). Liste bir çıktı belgesidir: kaydedilince değişmez; yanlışsa
 * yeni liste oluşturulur, eskisi geçmişte kalır.
 *
 * - Üst alanlar [FAL Form 5 kutu 1–5]: 1.1 Name of ship, 1.2 IMO number, 1.3 Call sign, 1.4 Voyage
 *   number, 2 Port of arrival/departure, 3 Date of arrival/departure, 4 Flag State, 5 Last port of
 *   call. Gemi kaydından ve seferden önceden dolar, düzenlenebilir; boş kalabilir.
 * - Satırlar [kutu 6–11]: No., Family name, given names, Rank or rating, Nationality, Date and place
 *   of birth, Nature and number of identity document. Her satır gemideki bir kişidir (`userId`);
 *   yolcu satırı yok (ADR 0130 §B4: FAL Form 6 ayrı belgedir; misafirin kişisel verisi saklanmaz).
 * - Sıra satır sırasıdır (varsayılan rütbe kıdemi; kullanıcı değiştirebilir).
 */

export const CREW_LIST_DIRECTIONS = ['arrival', 'departure'] as const;
export type CrewListDirection = (typeof CREW_LIST_DIRECTIONS)[number];

/** Kimlik belgesi türü [FAL Form 5 kutu 11]: pasaport ya da gemiadamı cüzdanı (seaman's book). */
export const CREW_LIST_DOC_TYPES = ['passport', 'seaman_book'] as const;
export type CrewListDocType = (typeof CREW_LIST_DOC_TYPES)[number];

/** Bir listede en çok satır (yat ve küçük-orta filo; FAL formu sayfalanır). */
export const MAX_CREW_LIST_ROWS = 200;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const day = z
  .string(typed('INVALID_DATE'))
  .regex(DAY, 'INVALID_DATE')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'INVALID_DATE');
const optionalDay = z
  .union([z.literal(''), day])
  .nullish()
  .transform((value) => (value ? value : null));

/** İsteğe bağlı metin: kırpılır; boşsa `null`. */
const optional = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .nullish()
    .transform((value) => (value ? value : null));
const required = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const oneOf = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_VALUE'));

export const crewListRowSchema = z.strictObject({
  userId: z.uuid(typed('INVALID_ID')),
  familyName: required(100),
  givenNames: optional(150),
  rank: optional(100),
  nationality: optional(60),
  birthDate: optionalDay,
  birthPlace: optional(100),
  docType: oneOf(CREW_LIST_DOC_TYPES),
  docNumber: optional(40),
});
export type CrewListRow = z.output<typeof crewListRowSchema>;

export const crewListHeaderSchema = z.strictObject({
  vesselName: required(120),
  imo: optional(20),
  callSign: optional(20),
  voyageNo: optional(40),
  port: optional(100),
  date: optionalDay,
  flag: optional(60),
  lastPort: optional(100),
});
export type CrewListHeader = z.output<typeof crewListHeaderSchema>;

export const crewListSchema = z
  .strictObject({
    direction: oneOf(CREW_LIST_DIRECTIONS),
    voyageId: z
      .uuid(typed('INVALID_ID'))
      .nullish()
      .transform((value) => value ?? null),
    header: crewListHeaderSchema,
    rows: z
      .array(crewListRowSchema, typed('INVALID_VALUE'))
      .min(1, 'REQUIRED')
      .max(MAX_CREW_LIST_ROWS, 'TOO_MANY'),
  })
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    value.rows.forEach((row, index) => {
      if (seen.has(row.userId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'DUPLICATE_PERSON',
          path: ['rows', index, 'userId'],
        });
      }
      seen.add(row.userId);
    });
  });
export type CrewListInput = z.output<typeof crewListSchema>;

export function parseCrewList(input: unknown): ParseResult<CrewListInput> {
  const result = crewListSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

/**
 * Kayıttaki tam addan FAL'ın "Family name, given names" alanının önerisi: soyadı son sözcük.
 * Kullanıcı satırda düzeltir (birden çok soyadı, Asya ad sırası).
 */
export function splitFullName(fullName: string): { familyName: string; givenNames: string } {
  const words = collapse(fullName).split(' ').filter(Boolean);
  const familyName = words.pop() ?? '';
  return { familyName, givenNames: words.join(' ') };
}

/**
 * "Date and place of birth" [FAL Form 5 kutu 10]: yalnız dolu parçalar yazılır. Eski sistem boş
 * alana "null - null" basıyordu (ortağın örneği).
 */
export function crewListBirthText(
  birthDate: string | null,
  birthPlace: string | null,
  formatDay: (day: string) => string,
): string {
  return [birthDate ? formatDay(birthDate) : null, birthPlace]
    .filter((part): part is string => Boolean(part))
    .join(', ');
}
